import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { CustomSLButton, FlexRow, SmallText, UltraMediumText, SLNotification } from "../../components/core/utility";
import { CustomInput, CustomDropdown } from "./AddAVehicle";
import { RadioButtonGroup } from "@vds/inputs";
import styled from "styled-components";
import { ADDRESS_REGEX, ZIP_CODE_LIMIT, ZIP_REGEX } from "../../constants/C4SValidationConstants";
import { css } from "@emotion/react";
import { groupBy } from "lodash";
import { validateAddress } from "../../services/C4S Decommission - Replacement/validateAddress";
import { getStatesList } from "../../services/C4S Decommission - Replacement/getStatesList";
import { getCountryList } from "../../services/scheduler/getCountryList";
import { generate } from "shortid";
import { C4SContext } from "../../context/c4sContext";
import useOutsideClick from "../../customHook/use-outside-click";
import { Loader } from "@vds/loaders";
import "./c4sDecomCustom.css";

const RadioButtonGroupWrapper = styled.div`
  div[role="radiogroup"] {
    border: 0.0625rem solid #d8dada;
    width: ${(props) => props.$width || "70vmin"};
    margin-bottom: 0.6rem;
    > div > label {
      > div:first-child > div {
        height: 1rem;
        width: 1rem;
        transform: translate(50%, 100%);
      }
      > div:last-child {
        font-size: 12px;
        font-family: Verizon-NHG-eTX;
        padding: 0.313rem 0.5rem;
        > span > span {
          font-size: 14px;
          font-family: Verizon-NHG-eTX;
        }
      }
    }
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
`;

const CustomHistoricalLoader = css`
  display: flex;
  flex-direction: row;
  margin-left: 1rem;
  margin-top: 0.5rem;
  div[aria-label="loader overlay"] {
    position: relative;
    width: 1.5rem;
    height: 1.5rem;
    > div {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
  & div {
    padding-left: 0.5rem;
  }
`;

const AddressContainer = styled.div`
  width: ${(props) => props.$width || "100%"};
  box-sizing: border-box;
`;

const COUNTRY_ZIP_LIMITS = {
  DEU: [5, 5],
  GBR: [4, 8],
  FRA: [5, 5],
  CAN: [6, 6],
  NLD: [4, 4],
  IRL: [7, 7],
  MEX: [5, 5],
  NZL: [4, 4],
  USA: [5, 5],
  AUS: [4, 4],
  AUT: [4, 4],
  BEL: [4, 4],
  MCO: [5, 5],
  IOM: [4, 8],
};

export const CommonValidateAddress = ({
  address,
  setAddress,
  setLoader = () => {},
  showHistoricalAddressDropdown = false,
  vehicleData = null,
  shippingAddress = "",
  autoValidateShippingAddress = false,
  defaultCountryOnNew = null,
  defaultCountryOnInit = null,
  trackLocusFields = false,
  showTypedAddressOption = true,
  showInternalLoader = false,
  showTitle = true,
  title = "Shipping Address",
  subTitle = "",
  notify: externalNotify = null,
  setValidAddress = null,
  modal = false,
  callBackNotify = null,
  onVerificationComplete = null,
  setModalOpened = null,
  setAlertModal = null,
  onFieldsChanged = null,
  variant = "default",
  styles = {},
}) => {
  const { notification, historicalAddressDetails, countryListDetails, stateListDetails } = useContext(C4SContext);
  const { verified, addressByPass, historicalAddress, address1, address2, city, state, zip, country } = address;

  const [isAddressSuggestionShown, setIsAddressSuggestionShown] = useState(false);
  const [isAddressFieldsDisable, setIsAddressFieldsDisable] = useState(showHistoricalAddressDropdown);
  const [suggestedAddress, setSuggestedAddress] = useState({
    address: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });
  const [isLoading, setInternalLoader] = useState(false);
  const [typedAddress, setTypedAddress] = useState("");
  const [isAddressValidationBtnDisabled, setIsAddressValidationBtnDisabled] = useState(true);
  const [zipCodeLimit, setZipCodeLimit] = useState(ZIP_CODE_LIMIT);
  const [zipRegex, setZipRegex] = useState(ZIP_REGEX);
  const [selectedAddressType, setSelectedAddressType] = useState("");
  const [dpvStatus, setDpvStatus] = useState(null);
  const [dpvWarning, setDpvWarning] = useState(null);
  const [liveAddressPreview, setLiveAddressPreview] = useState("");
  const [isInSdFlow, setIsInSdFlow] = useState(false);
  const skipAutoValidateRef = useRef(false);
  const sdPendingAddressRef = useRef(null);

  const isEditVehicle = variant === "editVehicle";
  const [originalValues] = useState(() => {
    if (!isEditVehicle) return {};
    const parsed = (() => {
      const parts = (shippingAddress || "").split(",");
      let a1 = "",
        a2 = "",
        c = "",
        s = "",
        z = "",
        co = "";
      if (parts.length <= 6) {
        a1 = parts[0]?.trim() || "";
        c = parts[parts.length - 4]?.trim() || "";
        s = parts[parts.length - 3]?.trim() || "";
        z = parts[parts.length - 2]?.trim() || "";
        co = parts[parts.length - 1]?.trim() || "";
        if (parts.length > 5) a2 = parts.slice(1, -4).join(",").trim();
      }
      return { address1: a1, address2: a2, city: c, state: s, zipCode: z, country: co };
    })();
    return { ...parsed, historicalAddress: shippingAddress || "" };
  });
  const [isValidatingHistoricalAddress, setIsValidatingHistoricalAddress] = useState(false);
  const [isValidatingNewAddress, setIsValidatingNewAddress] = useState(false);
  const [handleInvalidAddress, setHandleInvalidAddress] = useState(true);
  const isErrorInAddressFields = useMemo(
    () => address1.error || address2.error || city.error || state.error || country.error || zip.error,
    [address1, address2, city, state, country, zip]
  );

  const handleClickOutside = () => {
    notification?.setNotification(null);
  };
  const clickRef = useOutsideClick(handleClickOutside);

  const clearNotification = () => {
    if (externalNotify) {
      externalNotify(null);
    } else if (modal && callBackNotify) {
      callBackNotify(null, null, false);
    } else {
      notification?.setNotification(null);
    }
  };

  const notify = (content, type) => {
    if (externalNotify) {
      externalNotify(content, type);
    } else if (modal && callBackNotify) {
      callBackNotify(content, type, true);
    } else {
      notification?.setNotification(
        <SLNotification
          key={generate()}
          type={type}
          title={content}
          fullBleed={true}
          ref={clickRef}
          onCloseButtonClick={() => notification?.setNotification(null)}
        />
      );
    }
  };

  // ── Utility ────────────────────────────────────────────────────────────

  const splitAddress = (addr) => {
    const parts = addr.split(",");
    let a1 = "",
      a2 = "",
      c = "",
      s = "",
      z = "",
      co = "";
    if (parts.length <= 6) {
      a1 = parts[0].trim();
      c = parts[parts.length - 4].trim();
      s = parts[parts.length - 3].trim();
      z = parts[parts.length - 2].trim();
      co = parts[parts.length - 1].trim();
      if (parts.length > 5) a2 = parts.slice(1, -4).join(",").trim();
    }
    return { address1: a1, address2: a2, city: c, state: s, zipCode: z, country: co };
  };

  const handleZipCodeBasedOnCountry = (countryCode) => {
    let zipCodeRegex = ZIP_REGEX,
      limit = ZIP_CODE_LIMIT;
    if (countryCode) {
      const limits = COUNTRY_ZIP_LIMITS[countryCode];
      zipCodeRegex = new RegExp(`^\\d{${limits[0]},${limits[1]}}$`);
      limit = limits[1];
      if (["GBR", "CAN", "IRL", "NLD", "IOM"].includes(countryCode)) {
        if (countryCode === "IRL") {
          zipCodeRegex = new RegExp("^[a-zA-Z0-9]{3} [a-zA-Z0-9]{4}$");
          limit = limits[1] + 1;
        }
        if (countryCode === "CAN") {
          zipCodeRegex = new RegExp("^[a-zA-Z]\\d[a-zA-Z] \\d[a-zA-Z]\\d$");
          limit = limits[1] + 1;
        }
        if (countryCode === "GBR" || countryCode === "IOM") {
          zipCodeRegex = new RegExp(`^[a-zA-Z0-9 ]{${limits[0]},${limits[1]}}$`);
          limit = limits[1];
        }
        if (countryCode === "NLD") {
          zipCodeRegex = new RegExp("^[0-9]{4} [a-zA-Z]{2}$");
          limit = 7;
        }
      } else {
        limit = limits[1];
      }
    }
    setZipRegex(zipCodeRegex);
    setZipCodeLimit(limit);
    return { zipCodeRegex };
  };

  const upsertHistoricalAddress = (formatted, replaceAddr = null) => {
    if (!formatted) return;
    const upper = formatted.toUpperCase();
    if (historicalAddressDetails?.historicalAddressDetails) {
      let list = historicalAddressDetails.historicalAddressDetails;
      if (replaceAddr) list = list.filter((a) => a.toUpperCase() !== replaceAddr.toUpperCase());
      if (!list.some((a) => a.toUpperCase() === upper)) {
        historicalAddressDetails.setHistoricalAddressDetails([...list, upper]);
      } else if (replaceAddr) {
        historicalAddressDetails.setHistoricalAddressDetails(list);
      }
    }
  };

  const isSuggestionInDropdown = (suggestedAddr) => {
    if (!showHistoricalAddressDropdown || !suggestedAddr) return false;
    const norm = suggestedAddr.toLowerCase();
    if (shippingAddress && shippingAddress.toLowerCase() === norm) return true;
    return historicalAddressDetails?.historicalAddressDetails?.some((a) => a.toLowerCase() === norm) || false;
  };

  // ── Data fetching ──────────────────────────────────────────────────────

  const getCountries = async () => {
    let countryOptions = [],
      allCountryStateOptions = null;
    const countryListResponse = countryListDetails?.countryListDetails ? countryListDetails.countryListDetails : await getCountryList();
    if (countryListResponse.status === 200 && countryListResponse?.data?.data?.length) {
      countryOptions = countryListResponse.data.data;
      countryListDetails.setCountryListDetails(countryListResponse);
      ({ allCountryStateOptions } = await getStateListData());
    } else {
      notify("Unable to fetch country list due to an unexpected error", "error");
    }
    setAddress((prev) => ({
      ...prev,
      country: {
        ...prev.country,
        options: countryOptions,
        error: !countryOptions.length,
        errorText: countryOptions.length ? prev.country.errorText : "Unable to fetch country list due to an unexpected error",
        success: false,
      },
      state: {
        ...prev.state,
        options: [],
        allStateOptions: allCountryStateOptions,
        error: !allCountryStateOptions,
        errorText: allCountryStateOptions ? prev.state.errorText : "Unable to fetch state list due to an unexpected error",
        success: false,
      },
    }));
    return { countryOptions, allCountryStateOptions };
  };

  const getStateListData = async () => {
    let allCountryStateOptions = null;
    const stateListResponse = stateListDetails.stateListDetails ? stateListDetails.stateListDetails : await getStatesList();
    if (stateListResponse.status === 200 && stateListResponse?.data?.data?.length) {
      stateListDetails.setStateListDetails(stateListResponse);
      allCountryStateOptions = stateListResponse.data.data.map((st) => ({ ...st, stateName: st.erpStateCode + " - " + st.stateCode }));
      allCountryStateOptions = groupBy(allCountryStateOptions, "countryCode");
    } else {
      notify("Unable to fetch state list due to an unexpected error", "error");
    }
    return { allCountryStateOptions };
  };

  // ── Field handlers ─────────────────────────────────────────────────────

  const handleAddressFields = (
    field,
    value,
    customValue1 = false,
    customValue2 = false,
    countryOptions = false,
    allCountryStateOptions = false,
    trim = false
  ) => {
    if (!trim && verified) {
      setAddress((prev) => ({ ...prev, verified: false }));
    }
    switch (field) {
      case "Address 1": {
        if (value && value.length && value.toLowerCase() !== "null") {
          ADDRESS_REGEX.test(value)
            ? setAddress((prev) => ({ ...prev, address1: { value, error: false, errorText: "Enter address 1", success: true } }))
            : setAddress((prev) => ({ ...prev, address1: { value, error: true, errorText: "Enter a valid address 1", success: false } }));
        } else {
          setAddress((prev) => ({ ...prev, address1: { value: "", error: true, errorText: "Enter address 1", success: false } }));
        }
        break;
      }
      case "Address 2": {
        if (value && value.length && value.toLowerCase() !== "null") {
          ADDRESS_REGEX.test(value)
            ? setAddress((prev) => ({ ...prev, address2: { value, error: false, errorText: "Enter address 2", success: true } }))
            : setAddress((prev) => ({ ...prev, address2: { value, error: true, errorText: "Enter a valid address 2", success: false } }));
        } else {
          setAddress((prev) => ({ ...prev, address2: { value: "", error: false, errorText: "Enter address 2", success: false } }));
        }
        break;
      }
      case "City": {
        value && value.length && value.toLowerCase() !== "null"
          ? ADDRESS_REGEX.test(value)
            ? setAddress((prev) => ({ ...prev, city: { ...prev.city, value, error: false, errorText: "Enter city", success: true } }))
            : setAddress((prev) => ({ ...prev, city: { ...prev.city, value, error: true, errorText: "Enter valid city", success: false } }))
          : setAddress((prev) => ({ ...prev, city: { ...prev.city, value: "", error: true, errorText: "Enter city", success: false } }));
        break;
      }
      case "State": {
        value = customValue1.find((st) => value === st.erpStateCode || value?.toLowerCase() === st.stateCode.toLowerCase()) ?? null;
        value
          ? setAddress((prev) => ({ ...prev, state: { ...prev.state, value, error: false, errorText: "Please select state", success: true } }))
          : setAddress((prev) => ({ ...prev, state: { ...prev.state, value: null, error: true, errorText: "Please select state", success: false } }));
        break;
      }
      case "Zip": {
        value = value.replace(/[^a-zA-Z0-9 ]/g, "");
        if (value && value.length && value.toLowerCase() !== "null") {
          customValue1.test(value)
            ? setAddress((prev) => ({ ...prev, zip: { value, error: false, errorText: "Enter zip code", success: true } }))
            : setAddress((prev) => ({ ...prev, zip: { value, error: true, errorText: "Enter a valid zip code", success: false } }));
        } else {
          setAddress((prev) => ({ ...prev, zip: { value: "", error: true, errorText: "Enter zip code", success: false } }));
        }
        break;
      }
      case "Country": {
        countryOptions = countryOptions || country.options;
        let stateOptions = allCountryStateOptions || state.allStateOptions;
        value =
          countryOptions.find((c) => c.code.toLowerCase() === value?.toLowerCase() || c.description.toLowerCase() === value?.toLowerCase()) ?? null;
        const { zipCodeRegex } = handleZipCodeBasedOnCountry(value?.code ?? null);
        if (value) {
          stateOptions = stateOptions ? stateOptions[value.code] : [];
          setAddress((prev) => ({
            ...prev,
            country: { ...prev.country, value, error: false, errorText: "Please select country", success: true },
            state: {
              ...prev.state,
              value: null,
              options: stateOptions,
              error: !stateOptions.length || prev.state.value || prev.state.error ? true : false,
              success: false,
            },
          }));
          stateOptions.length && customValue1 && handleAddressFields("State", customValue1, stateOptions);
        } else {
          setAddress((prev) => ({
            ...prev,
            country: {
              ...prev.country,
              value: null,
              error: true,
              errorText: prev.country.error ? prev.country.errorText : "Please select country",
              success: false,
            },
            state: { ...prev.state, value: null, options: [], error: prev.state.value || prev.state.error ? true : false, success: false },
          }));
        }
        customValue2 ? handleAddressFields("Zip", customValue2, zipCodeRegex) : zip.value && handleAddressFields("Zip", zip.value, zipCodeRegex);
        break;
      }
    }
  };

  // ── Initialization ─────────────────────────────────────────────────────

  const handleAddress = async () => {
    skipAutoValidateRef.current = true;
    if (isEditVehicle) {
      if (vehicleData && Object.keys(vehicleData).length) {
        setAddress((prev) => ({ ...prev, historicalAddress: { value: shippingAddress, error: false, errorText: "", success: true } }));
        handleAddressFields("Address 1", vehicleData.addressOne?.trim());
        handleAddressFields("Address 2", vehicleData.addressTwo?.trim());
        handleAddressFields("City", vehicleData.city?.trim());
        handleAddressFields("Country", vehicleData.country?.trim(), vehicleData.stateCode?.trim(), vehicleData.zipCode?.trim());
        setAddress((prev) => ({ ...prev, addressByPass: "NeedToValidateOnLoad" }));
      }
      return;
    }
    if (showInternalLoader) setInternalLoader(true);
    else setLoader(true);
    let countryOptions = [],
      allCountryStateOptions = null;
    if (address.country.value && address.country.options.length) {
      countryOptions = address.country.options;
      handleZipCodeBasedOnCountry(address.country.value.code);
      if (address.state.value && address.state.options.length && address.state.allStateOptions)
        allCountryStateOptions = address.state.allStateOptions;
    } else {
      ({ countryOptions, allCountryStateOptions } = await getCountries());
    }
    if (showInternalLoader) setInternalLoader(false);
    else setLoader(false);

    if (vehicleData && Object.keys(vehicleData).length) {
      setAddress((prev) => ({
        ...prev,
        historicalAddress: { value: "Please select an address or choose Add New to add a new address", error: false, errorText: "", success: true },
      }));
      handleAddressFields("Address 1", vehicleData.addressOne?.trim());
      handleAddressFields("Address 2", vehicleData.addressTwo?.trim());
      handleAddressFields("City", vehicleData.city?.trim());
      handleAddressFields(
        "Country",
        vehicleData.country?.trim(),
        vehicleData.stateCode?.trim(),
        vehicleData.zipCode?.trim(),
        countryOptions,
        allCountryStateOptions
      );
      setAddress((prev) => ({ ...prev, addressByPass: "NeedToValidateOnLoad" }));
    }
    if (defaultCountryOnInit && countryOptions.length && !address.country.value) {
      handleAddressFields("Country", defaultCountryOnInit, false, false, countryOptions, allCountryStateOptions);
    }
  };

  const validateAndSetShippingAddress = async (addressToValidate, { silent = false } = {}) => {
    if (!addressToValidate || addressToValidate.trim() === "") return;
    clearNotification();
    setDpvWarning(null);
    if (isEditVehicle) setIsValidatingHistoricalAddress(true);
    if (showInternalLoader) setInternalLoader(true);
    setAddress((prev) => ({ ...prev, historicalAddress: { value: addressToValidate, error: false, errorText: "", success: true } }));
    setSelectedAddressType(addressToValidate);
    const splitted = splitAddress(addressToValidate);
    try {
      const data = {
        inputAddress: {
          addressLine1: splitted.address1,
          addressLine2: splitted.address2,
          city: splitted.city,
          stateProvince: splitted.state,
          postalCode: splitted.zipCode,
          country: splitted.country,
        },
      };
      const response = await validateAddress(data);
      if (response?.status === 200 && response?.data?.message?.toLowerCase() === "success") {
        const rd = response.data.data;
        const byPass = rd?.addressByPass;
        const locFound = rd?.locationFound;
        const stdAddr = rd?.standardizedInfo?.standardizedAddress;
        const userAddr = rd?.inputAddress;
        const dpvValue = (rd?.location?.dpv || "").toUpperCase().trim();
        const dpvDetail = rd?.location?.dpvDetail || "";
        const timezone = rd?.location?.timezone ?? "";
        const locusExtra = trackLocusFields
          ? {
              latitude: stdAddr?.latLong?.latitude ?? "",
              longitude: stdAddr?.latLong?.longitude ?? "",
              locusSuggestedAddress: stdAddr?.formattedAddress,
              locusLocationId: rd?.location?.locationId,
              dpv: rd?.location?.dpv ?? "",
              dpvDetail: rd?.location?.dpvDetail ?? "",
            }
          : {};

        const baseFields = {
          address1: { value: splitted.address1, error: false, errorText: "Enter address 1", success: true },
          address2: { value: splitted.address2, error: false, errorText: "Enter address 2", success: false },
          city: { value: splitted.city, error: false, errorText: "Enter city", success: true },
          countryCode: splitted.country,
          addressByPass: byPass,
          locationFound: locFound,
          timezone,
          ...locusExtra,
        };
        const stateAndCountry = (prev) => {
          let stateOpts = prev.state.allStateOptions?.[splitted.country] || prev.state.options || [];
          let resolvedAllStateOptions = prev.state.allStateOptions;
          if (!stateOpts.length && stateListDetails?.stateListDetails?.data?.data) {
            const mapped = stateListDetails.stateListDetails.data.data.map((st) => ({
              ...st,
              stateName: st.erpStateCode + " - " + st.stateCode,
            }));
            resolvedAllStateOptions = groupBy(mapped, "countryCode");
            stateOpts = resolvedAllStateOptions[splitted.country] || [];
          }
          const resolvedState =
            stateOpts.find((st) => st.erpStateCode === splitted.state || st.stateCode?.toLowerCase() === splitted.state?.toLowerCase()) ||
            (splitted.state ? { erpStateCode: splitted.state, stateCode: splitted.state, stateName: splitted.state } : null);
          return {
            state: {
              ...prev.state,
              value: resolvedState,
              options: stateOpts.length ? stateOpts : prev.state.options,
              allStateOptions: resolvedAllStateOptions || prev.state.allStateOptions,
              error: false,
              errorText: "Please select state",
              success: true,
            },
            country: { ...prev.country, value: { code: splitted.country }, error: false, errorText: "Please select country", success: true },
            zip: { value: splitted.zipCode, error: false, errorText: "Enter zip code", success: true },
          };
        };

        const suggestionAddr = `${stdAddr?.addressLine1}, ${stdAddr?.subLocation ? stdAddr.subLocation + ", " : ""}${stdAddr?.city}, ${
          stdAddr?.stateProvince
        }, ${stdAddr?.postalCodeBase}, ${stdAddr?.iso3CountryCode}`;
        const typedAddr = `${userAddr?.addressLine1}, ${userAddr?.addressLine2 ? userAddr.addressLine2 + ", " : ""}${userAddr?.city}, ${
          userAddr?.stateProvince
        }, ${userAddr?.postalCode}, ${userAddr?.country}`;

        if (byPass === "YES") {
          setAddress((prev) => ({ ...prev, ...baseFields, ...stateAndCountry(prev), verified: true }));
          if (!locFound && !silent) notify("Please note that the entered address is not verified and couldn't find a close match", "warning");
        } else if (dpvValue === "Y") {
          if (isSuggestionInDropdown(suggestionAddr)) {
            setAddress((prev) => ({
              ...prev,
              ...baseFields,
              address1: { value: stdAddr?.addressLine1 || splitted.address1, error: false, errorText: "Enter address 1", success: true },
              address2: { value: stdAddr?.subLocation || "", error: false, errorText: "Enter address 2", success: false },
              city: { value: stdAddr?.city || splitted.city, error: false, errorText: "Enter city", success: true },
              ...stateAndCountry(prev),
              verified: true,
            }));
          } else {
            upsertHistoricalAddress(suggestionAddr);
            setSelectedAddressType(suggestionAddr);
            setAddress((prev) => ({
              ...prev,
              ...baseFields,
              address1: { value: stdAddr?.addressLine1 || splitted.address1, error: false, errorText: "Enter address 1", success: true },
              address2: { value: stdAddr?.subLocation || "", error: false, errorText: "Enter address 2", success: false },
              city: { value: stdAddr?.city || splitted.city, error: false, errorText: "Enter city", success: true },
              ...stateAndCountry(prev),
              historicalAddress: { value: suggestionAddr, error: false, errorText: "", success: true },
              verified: true,
            }));
          }
        } else if (dpvValue === "U" || dpvValue === "V" || dpvValue === "") {
          if (!silent) notify(dpvDetail || "The address could not be verified. Please check the address and try again.", "error");
          setAddress((prev) => ({ ...prev, ...baseFields, ...stateAndCountry(prev) }));
          setIsAddressFieldsDisable(false);
          setIsAddressValidationBtnDisabled(false);
        } else if (dpvValue === "S" || dpvValue === "D") {
          setSelectedAddressType(suggestionAddr);
          setDpvStatus(dpvValue);
          setAddress((prev) => ({
            ...prev,
            ...baseFields,
            address1: { value: stdAddr?.addressLine1 || splitted.address1, error: false, errorText: "Enter address 1", success: true },
            address2: { value: stdAddr?.subLocation || "", error: true, errorText: dpvDetail || "Please correct Address 2", success: false },
            city: { value: stdAddr?.city || splitted.city, error: false, errorText: "Enter city", success: true },
            ...stateAndCountry(prev),
            historicalAddress: { value: suggestionAddr, error: false, errorText: "", success: true },
            verified: false,
          }));
          setIsAddressFieldsDisable(false);
          setIsAddressValidationBtnDisabled(false);
        } else if (dpvValue === "M" || dpvValue === "N") {
          setSelectedAddressType(suggestionAddr);
          setDpvStatus(dpvValue);
          setDpvWarning(dpvDetail || "Please review and update the address details.");
          setAddress((prev) => ({
            ...prev,
            ...baseFields,
            address1: { value: stdAddr?.addressLine1 || splitted.address1, error: false, errorText: "Enter address 1", success: true },
            address2: { value: stdAddr?.subLocation || "", error: false, errorText: "Enter address 2", success: false },
            city: { value: stdAddr?.city || splitted.city, error: false, errorText: "Enter city", success: true },
            ...stateAndCountry(prev),
            historicalAddress: { value: suggestionAddr, error: false, errorText: "", success: true },
            verified: false,
          }));
          setIsAddressFieldsDisable(false);
          setIsAddressValidationBtnDisabled(false);
        } else {
          if (!silent) notify(dpvDetail || "Address validation returned an unexpected result. Please try again.", "error");
          setAddress((prev) => ({ ...prev, ...baseFields, ...stateAndCountry(prev) }));
          setIsAddressFieldsDisable(false);
          setIsAddressValidationBtnDisabled(false);
        }

        if (isEditVehicle) {
          const isResolved = byPass === "YES" || dpvValue === "Y" || dpvValue === "S" || dpvValue === "D" || dpvValue === "M" || dpvValue === "N";
          setHandleInvalidAddress(!isResolved);
        }
        if (onVerificationComplete) onVerificationComplete(response);
      } else if (response?.data?.data?.errors || response?.response?.data?.message === "Unable to validate address") {
        if (!silent) notify("Address validation failed", "error");
      } else if (isEditVehicle) {
        setModalOpened?.(false);
        setAlertModal?.(true);
      }
    } catch (error) {
      if (isEditVehicle) {
        setModalOpened?.(false);
        setAlertModal?.(true);
      }
    } finally {
      if (isEditVehicle) setIsValidatingHistoricalAddress(false);
    }
    if (showInternalLoader) setInternalLoader(false);
    else setLoader(false);
  };

  // ── Address verification ───────────────────────────────────────────────

  const handleAddressVerification = async (addressData, autoSelect = false) => {
    skipAutoValidateRef.current = true;
    clearNotification();
    setIsValidatingNewAddress(true);
    if (showInternalLoader) {
      setInternalLoader(true);
    }
    showHistoricalAddressDropdown && !isAddressFieldsDisable && setIsAddressFieldsDisable(true);
    !isAddressValidationBtnDisabled && setIsAddressValidationBtnDisabled(true);
    setDpvStatus(null);
    setDpvWarning(null);

    const data = {
      inputAddress: {
        addressLine1: addressData?.address1?.value,
        addressLine2: addressData?.address2?.value ?? "",
        city: addressData?.city?.value,
        stateProvince: addressData?.state?.value?.erpStateCode,
        postalCode: addressData?.zip?.value,
        country: addressData?.country?.value?.code,
      },
    };
    try {
      const response = await validateAddress(data);
      let timezone = "",
        latitude = "",
        longitude = "",
        locusSuggestedAddress = "",
        locusLocationId = "",
        dpv = "",
        dpvDetailValue = "";

      if (response?.status === 200 && response?.data?.message?.toLowerCase() === "success") {
        const rd = response?.data?.data,
          byPass = rd?.addressByPass,
          locFound = rd?.locationFound,
          stdAddr = rd?.standardizedInfo?.standardizedAddress,
          userAddr = rd?.inputAddress;

        timezone = rd?.location?.timezone ?? "";
        if (trackLocusFields) {
          latitude = rd?.standardizedInfo?.standardizedAddress?.latLong?.latitude ?? "";
          longitude = rd?.standardizedInfo?.standardizedAddress?.latLong?.longitude ?? "";
          locusSuggestedAddress = `${stdAddr?.addressLine1}, ${stdAddr?.subLocation ? stdAddr?.subLocation + ", " : ""}${stdAddr?.city}, ${
            stdAddr?.stateProvince
          }, ${stdAddr?.postalCodeBase}, ${stdAddr?.iso3CountryCode}`;
          locusLocationId = rd?.location?.locationId ?? "";
          dpv = rd?.location?.dpv ?? "";
          dpvDetailValue = rd?.location?.dpvDetail ?? "";
        }

        const locusExtra = trackLocusFields ? { latitude, longitude, locusSuggestedAddress, locusLocationId, dpv, dpvDetail: dpvDetailValue } : {};
        const dpvValue = (rd?.location?.dpv || "").toUpperCase().trim();
        const dpvDetail = rd?.location?.dpvDetail || "";

        const suggestionAddr = `${stdAddr?.addressLine1}, ${stdAddr?.subLocation ? stdAddr?.subLocation + ", " : ""}${stdAddr?.city}, ${
          stdAddr?.stateProvince
        }, ${stdAddr?.postalCodeBase}, ${stdAddr?.iso3CountryCode}`;
        const typedAddr = `${userAddr.addressLine1}, ${userAddr.addressLine2 ? userAddr.addressLine2 + ", " : ""}${userAddr.city}, ${
          userAddr.stateProvince
        }, ${userAddr.postalCode}, ${userAddr.country}`;

        const resolvedAutoSelectState = autoSelect
          ? (() => {
              const stCode = stdAddr?.stateProvince;
              const cCode = stdAddr?.iso3CountryCode;
              let opts = state.allStateOptions?.[cCode] || state.options || [];
              if (!opts.length && stateListDetails?.stateListDetails?.data?.data) {
                const mapped = stateListDetails.stateListDetails.data.data.map((st) => ({
                  ...st,
                  stateName: st.erpStateCode + " - " + st.stateCode,
                }));
                opts = groupBy(mapped, "countryCode")[cCode] || [];
              }
              return (
                opts.find((st) => st.erpStateCode === stCode || st.stateCode?.toLowerCase() === stCode?.toLowerCase()) ||
                (stCode ? { erpStateCode: stCode, stateCode: stCode, stateName: stCode } : state.value)
              );
            })()
          : null;

        if (byPass === "YES") {
          setAddress((prev) => ({ ...prev, verified: true, addressByPass: byPass, locationFound: locFound, timezone, ...locusExtra }));
          if (!locFound) notify("Please note that the entered address is not verified and couldn't find a close match", "warning");
        } else if (dpvValue === "Y") {
          if (isSuggestionInDropdown(suggestionAddr)) {
            const addr2Val = stdAddr?.subLocation || addressData?.address2?.value || "";
            const verifiedFmt = `${stdAddr?.addressLine1}, ${addr2Val ? addr2Val + ", " : ""}${stdAddr?.city}, ${stdAddr?.stateProvince}, ${
              stdAddr?.postalCodeBase
            }, ${stdAddr?.iso3CountryCode}`;
            handleAddressFields("Address 1", stdAddr?.addressLine1);
            handleAddressFields("Address 2", addr2Val);
            handleAddressFields("City", stdAddr?.city);
            handleAddressFields("Country", stdAddr?.iso3CountryCode, stdAddr?.stateProvince, stdAddr?.postalCodeBase);
            if (sdPendingAddressRef.current) {
              upsertHistoricalAddress(verifiedFmt, sdPendingAddressRef.current);
              sdPendingAddressRef.current = null;
              setSelectedAddressType(verifiedFmt);
              setAddress((prev) => ({
                ...prev,
                verified: true,
                addressByPass: byPass,
                locationFound: locFound,
                timezone,
                ...locusExtra,
                historicalAddress: { value: verifiedFmt, error: false, errorText: "", success: true },
              }));
            } else {
              setAddress((prev) => ({ ...prev, verified: true, addressByPass: byPass, locationFound: locFound, timezone, ...locusExtra }));
            }
          } else if (autoSelect) {
            upsertHistoricalAddress(suggestionAddr);
            setSelectedAddressType(suggestionAddr);
            setAddress((prev) => ({
              ...prev,
              addressByPass: byPass,
              locationFound: locFound,
              timezone,
              ...locusExtra,
              address1: { value: stdAddr?.addressLine1 || prev.address1.value, error: false, errorText: "Enter address 1", success: true },
              address2: { value: stdAddr?.subLocation || "", error: false, errorText: "Enter address 2", success: false },
              city: { value: stdAddr?.city || prev.city.value, error: false, errorText: "Enter city", success: true },
              state: { ...prev.state, value: resolvedAutoSelectState, error: false, success: true },
              country: { ...prev.country, error: false, success: true },
              zip: { value: stdAddr?.postalCodeBase || prev.zip.value, error: false, errorText: "Enter zip code", success: true },
              historicalAddress: { value: suggestionAddr, error: false, errorText: "", success: true },
              verified: true,
            }));
          } else {
            setAddress((prev) => ({ ...prev, addressByPass: byPass, locationFound: locFound, timezone, ...locusExtra }));
            setSuggestedAddress({
              address: suggestionAddr,
              address1: stdAddr?.addressLine1,
              address2: stdAddr?.subLocation,
              city: stdAddr?.city,
              state: stdAddr?.stateProvince,
              zip: stdAddr?.postalCodeBase,
              country: stdAddr?.iso3CountryCode,
              ...(trackLocusFields ? { locusLocationId, dpv, dpvDetail: dpvDetailValue } : {}),
              dpvDetail,
            });
            setTypedAddress(typedAddr);
            setDpvStatus("Y_ADDR2");
            setIsAddressSuggestionShown(true);
            setIsAddressFieldsDisable(true);
            setIsAddressValidationBtnDisabled(true);
            if ((autoValidateShippingAddress || isEditVehicle) && historicalAddress.value === "Add New") {
              upsertHistoricalAddress(suggestionAddr);
            }
          }
        } else if (dpvValue === "U" || dpvValue === "V" || dpvValue === "") {
          notify(dpvDetail || "The address could not be verified. Please check the address and try again.", "error");
          setAddress((prev) => ({ ...prev, addressByPass: byPass, locationFound: locFound, timezone, ...locusExtra }));
          setIsAddressFieldsDisable(false);
          setIsAddressValidationBtnDisabled(false);
        } else if (dpvValue === "S" || dpvValue === "D" || dpvValue === "M" || dpvValue === "N") {
          if (autoSelect) {
            setSelectedAddressType(suggestionAddr);
            setDpvStatus(dpvValue);
            if (dpvValue === "S" || dpvValue === "D") {
              setAddress((prev) => ({
                ...prev,
                addressByPass: byPass,
                locationFound: locFound,
                timezone,
                ...locusExtra,
                address1: { value: stdAddr?.addressLine1 || prev.address1.value, error: false, errorText: "Enter address 1", success: true },
                address2: { value: stdAddr?.subLocation || "", error: true, errorText: dpvDetail || "Please correct Address 2", success: false },
                city: { value: stdAddr?.city || prev.city.value, error: false, errorText: "Enter city", success: true },
                state: { ...prev.state, value: resolvedAutoSelectState, error: false, success: true },
                country: { ...prev.country, error: false, success: true },
                zip: { value: stdAddr?.postalCodeBase || prev.zip.value, error: false, errorText: "Enter zip code", success: true },
                historicalAddress: { value: suggestionAddr, error: false, errorText: "", success: true },
                verified: false,
              }));
            } else {
              setDpvWarning(dpvDetail || "Please review and update the address details.");
              setAddress((prev) => ({
                ...prev,
                addressByPass: byPass,
                locationFound: locFound,
                timezone,
                ...locusExtra,
                address1: { value: stdAddr?.addressLine1 || prev.address1.value, error: false, errorText: "Enter address 1", success: true },
                address2: { value: stdAddr?.subLocation || "", error: false, errorText: "Enter address 2", success: false },
                city: { value: stdAddr?.city || prev.city.value, error: false, errorText: "Enter city", success: true },
                state: { ...prev.state, value: resolvedAutoSelectState, error: false, success: true },
                country: { ...prev.country, error: false, success: true },
                zip: { value: stdAddr?.postalCodeBase || prev.zip.value, error: false, errorText: "Enter zip code", success: true },
                historicalAddress: { value: suggestionAddr, error: false, errorText: "", success: true },
                verified: false,
              }));
            }
            setIsAddressFieldsDisable(false);
            setIsAddressValidationBtnDisabled(false);
          } else {
            setAddress((prev) => ({ ...prev, addressByPass: byPass, locationFound: locFound, timezone, ...locusExtra }));
            setSuggestedAddress({
              address: suggestionAddr,
              address1: stdAddr?.addressLine1,
              address2: stdAddr?.subLocation,
              city: stdAddr?.city,
              state: stdAddr?.stateProvince,
              zip: stdAddr?.postalCodeBase,
              country: stdAddr?.iso3CountryCode,
              ...(trackLocusFields ? { locusLocationId, dpv, dpvDetail: dpvDetailValue } : {}),
              dpvDetail,
            });
            setTypedAddress(typedAddr);
            setDpvStatus(dpvValue);
            if (dpvValue === "M" || dpvValue === "N") setDpvWarning(dpvDetail || "Please review and update the address details.");
            setIsAddressSuggestionShown(true);
            setIsAddressFieldsDisable(true);
            setIsAddressValidationBtnDisabled(true);
          }
        } else {
          notify(dpvDetail || "Address validation returned an unexpected result. Please try again.", "error");
          setAddress((prev) => ({ ...prev, addressByPass: byPass, locationFound: locFound, timezone, ...locusExtra }));
          setIsAddressFieldsDisable(false);
          setIsAddressValidationBtnDisabled(false);
        }

        if (shippingAddress && !isEditVehicle) {
          const entered = [
            addressData?.address1?.value,
            addressData?.address2?.value,
            addressData?.city?.value,
            addressData?.state?.value?.erpStateCode,
            addressData?.zip?.value,
            addressData?.country?.value?.code,
          ]
            .filter((item) => item !== null && item !== "")
            .join(", ");
          setAddress((prev) => ({ ...prev, isDefaultAddress: entered.toLowerCase() === shippingAddress.toLowerCase() }));
        }
        if (isEditVehicle) {
          const isResolved = byPass === "YES" || dpvValue === "Y" || dpvValue === "S" || dpvValue === "D" || dpvValue === "M" || dpvValue === "N";
          setHandleInvalidAddress(!isResolved);
        }
        if (onVerificationComplete) onVerificationComplete(response);
      } else {
        const locusExtra = trackLocusFields ? { latitude, longitude, locusSuggestedAddress, locusLocationId, dpv, dpvDetail: dpvDetailValue } : {};
        setAddress((prev) => ({ ...prev, timezone, ...locusExtra }));
        if (setValidAddress) setValidAddress({ isNewAddress: true });
        if (isEditVehicle) setHandleInvalidAddress(true);
        notify("Address validation failed due to an unexpected error", "error");
      }
    } catch (error) {
      if (isEditVehicle) setHandleInvalidAddress(true);
      notify("Error validating address. Please try again.", "error");
      setIsAddressFieldsDisable(false);
      setIsAddressValidationBtnDisabled(false);
    } finally {
      setIsValidatingNewAddress(false);
    }
    if (showInternalLoader) setInternalLoader(false);
  };

  // ── Address selection (suggested vs typed) ─────────────────────────────

  const handleAddressSelection = (isVerified = false) => {
    skipAutoValidateRef.current = true;
    if (isVerified) {
      const fmt = `${suggestedAddress.address1}, ${suggestedAddress.address2 ? suggestedAddress.address2 + ", " : ""}${suggestedAddress.city}, ${
        suggestedAddress.state
      }, ${suggestedAddress.zip}, ${suggestedAddress.country}`;
      if (!dpvStatus || dpvStatus === "Y_ADDR2") {
        upsertHistoricalAddress(fmt, sdPendingAddressRef.current);
        sdPendingAddressRef.current = null;
        setIsInSdFlow(false);
      } else if (dpvStatus === "S" || dpvStatus === "D") {
        sdPendingAddressRef.current = fmt;
        setIsInSdFlow(true);
      }
      setSelectedAddressType(fmt);
      const locusExtra =
        trackLocusFields && suggestedAddress.locusLocationId
          ? { locusLocationId: suggestedAddress.locusLocationId, dpv: suggestedAddress.dpv, dpvDetail: suggestedAddress.dpvDetail }
          : {};
      const isDefault = shippingAddress ? fmt === shippingAddress : undefined;

      handleAddressFields("Address 1", suggestedAddress.address1);
      handleAddressFields("Address 2", suggestedAddress.address2);
      handleAddressFields("City", suggestedAddress.city);
      handleAddressFields("Country", suggestedAddress.country, suggestedAddress.state, suggestedAddress.zip);

      if (dpvStatus === "Y_ADDR2") {
        handleAddressFields("Address 2", suggestedAddress.address2);
        setAddress((prev) => ({
          ...prev,
          historicalAddress: { value: fmt, error: false, errorText: "", success: true },
          verified: true,
          ...locusExtra,
          ...(isDefault !== undefined ? { isDefaultAddress: isDefault } : {}),
        }));
        setDpvStatus(null);
      } else if (dpvStatus === "S" || dpvStatus === "D") {
        setAddress((prev) => ({
          ...prev,
          historicalAddress: { value: fmt, error: false, errorText: "", success: true },
          verified: false,
          ...locusExtra,
          ...(isDefault !== undefined ? { isDefaultAddress: isDefault } : {}),
          address2: {
            ...prev.address2,
            value: suggestedAddress.address2 || prev.address2?.value || "",
            error: true,
            errorText: suggestedAddress.dpvDetail || "Please correct Address 2",
            success: false,
          },
        }));
        setIsAddressFieldsDisable(false);
        setIsAddressValidationBtnDisabled(false);
      } else if (dpvStatus === "M" || dpvStatus === "N") {
        setAddress((prev) => ({
          ...prev,
          historicalAddress: { value: fmt, error: false, errorText: "", success: true },
          verified: false,
          ...locusExtra,
          ...(isDefault !== undefined ? { isDefaultAddress: isDefault } : {}),
        }));
        setDpvWarning(suggestedAddress.dpvDetail || "Please review and update the address details.");
        setIsAddressFieldsDisable(false);
        setIsAddressValidationBtnDisabled(false);
      } else {
        setAddress((prev) => ({
          ...prev,
          historicalAddress: { value: fmt, error: false, errorText: "", success: true },
          verified: true,
          ...locusExtra,
          ...(isDefault !== undefined ? { isDefaultAddress: isDefault } : {}),
        }));
      }
    } else if (dpvStatus) {
      if (dpvStatus === "Y_ADDR2") {
        setAddress((prev) => ({ ...prev, verified: true }));
        setDpvStatus(null);
      } else if (dpvStatus === "S" || dpvStatus === "D") {
        setAddress((prev) => ({
          ...prev,
          verified: false,
          address2: {
            ...prev.address2,
            error: true,
            errorText: suggestedAddress.dpvDetail || "Please correct Address 2",
            success: false,
          },
        }));
        setIsAddressFieldsDisable(false);
        setIsAddressValidationBtnDisabled(false);
      } else if (dpvStatus === "M" || dpvStatus === "N") {
        setAddress((prev) => ({ ...prev, verified: false }));
        setDpvWarning(suggestedAddress.dpvDetail || "Please review and update the address details.");
        setIsAddressFieldsDisable(false);
        setIsAddressValidationBtnDisabled(false);
      }
    }
    setIsAddressSuggestionShown(false);
    setSuggestedAddress({ address: "", address1: "", address2: "", city: "", state: "", zip: "", country: "" });
    setTypedAddress("");
  };

  // ── Historical address selection ───────────────────────────────────────

  const handleHistoricalAddressSelection = async (addressSelected) => {
    if (selectedAddressType && addressSelected === selectedAddressType) return;
    if (liveAddressPreview && addressSelected === liveAddressPreview) return;
    setDpvStatus(null);
    setDpvWarning(null);
    setIsInSdFlow(false);
    sdPendingAddressRef.current = null;
    skipAutoValidateRef.current = false;
    if (shippingAddress && !isEditVehicle)
      setAddress((prev) => ({ ...prev, isDefaultAddress: addressSelected.toLowerCase() === shippingAddress.toLowerCase() }));
    if (setValidAddress) setValidAddress({ isNewAddress: addressSelected === "Add New", selectedAddress: addressSelected });

    if (isEditVehicle && addressSelected.toLowerCase() === shippingAddress?.toLowerCase()) {
      setIsValidatingHistoricalAddress(true);
      try {
        const stateValue =
          state.allStateOptions?.[originalValues.country]?.find(
            (s) => s.stateCode === originalValues.state || s.erpStateCode === originalValues.state
          ) || null;
        const stateOptions = state.allStateOptions?.[originalValues.country] || [];
        setAddress((prev) => ({
          ...prev,
          address1: { value: originalValues.address1, error: false, errorText: "Enter address 1", success: true },
          address2: { value: originalValues.address2, error: false, errorText: "Enter address 2", success: false },
          city: { value: originalValues.city, error: false, errorText: "Enter city", success: true },
          country: { ...prev.country, value: country.options.find((c) => c.code === originalValues.country) || null, error: false, success: true },
          state: { ...prev.state, value: stateValue?.erpStateCode, options: stateOptions, error: false, success: true },
          zip: { value: originalValues.zipCode, error: false, errorText: "Enter zip code", success: true },
          historicalAddress: { value: addressSelected, error: false, errorText: "", success: true },
          verified: true,
        }));
        setIsAddressFieldsDisable(true);
        setSelectedAddressType(addressSelected);
      } finally {
        setTimeout(() => setIsValidatingHistoricalAddress(false), 500);
      }
      return;
    }

    if (addressSelected === "Add New") {
      setAddress((prev) => ({
        ...prev,
        address1: { value: "", error: false, errorText: "Enter address 1", success: false },
        address2: { value: "", error: false, errorText: "Enter address 2", success: false },
        city: { value: "", error: false, errorText: "Enter city", success: false },
        state: {
          ...prev.state,
          value: null,
          options: [],
          error: !prev.state.allStateOptions,
          errorText: prev.state.options.length ? "Please select state" : prev.state.errorText,
          success: false,
        },
        zip: { value: "", error: false, errorText: "Enter zip code", success: false },
        country: {
          ...prev.country,
          value: null,
          error: !prev.country.options.length,
          errorText: prev.country.options.length ? "Please select country" : prev.country.errorText,
          success: false,
        },
        historicalAddress: {
          value: isEditVehicle ? "Add New" : "Please select an address or choose Add New to add a new address",
          error: false,
          errorText: "",
          success: false,
        },
        verified: false,
      }));
      if (defaultCountryOnNew) {
        handleAddressFields("Country", defaultCountryOnNew);
        setAddress((prev) => ({ ...prev, zip: { value: "", error: false, errorText: "Enter zip code", success: false } }));
      }
      setSelectedAddressType("Add New");
    } else {
      if (isEditVehicle) {
        setIsValidatingHistoricalAddress(true);
        setHandleInvalidAddress(true);
      }
      const splitted = splitAddress(addressSelected);
      handleAddressFields("Address 1", splitted.address1);
      handleAddressFields("Address 2", splitted.address2);
      handleAddressFields("City", splitted.city);
      handleAddressFields("Country", splitted.country, splitted.state, splitted.zipCode);
      setAddress((prev) => ({ ...prev, historicalAddress: { value: addressSelected, error: false, errorText: "", success: true }, verified: false }));
      setSelectedAddressType(addressSelected);
      if (isEditVehicle) {
        try {
          const valData = {
            inputAddress: {
              addressLine1: splitted.address1,
              addressLine2: splitted.address2,
              city: splitted.city,
              stateProvince: splitted.state,
              postalCode: splitted.zipCode,
              country: splitted.country,
            },
          };
          const response = await validateAddress(valData);
          if (response?.status === 200 && response?.data?.message.toLowerCase() === "success") {
            const rd = response.data.data;
            setAddress((prev) => ({
              ...prev,
              verified: true,
              addressByPass: rd.addressByPass,
              locationFound: rd.locationFound,
              timezone: rd.location?.timezone || "",
              ...(trackLocusFields
                ? {
                    latitude: rd.standardizedInfo?.standardizedAddress?.latLong?.latitude || "",
                    longitude: rd.standardizedInfo?.standardizedAddress?.latLong?.longitude || "",
                    locusSuggestedAddress: rd.standardizedInfo?.standardizedAddress?.formattedAddress,
                    locusLocationId: rd.location?.locationId,
                    dpv: rd.location?.dpv,
                    dpvDetail: rd.location?.dpvDetail,
                  }
                : {}),
            }));
          } else {
            notify("Address validation failed", "error");
          }
        } catch {
          notify("Error validating address", "error");
        } finally {
          setTimeout(() => setIsValidatingHistoricalAddress(false), 500);
        }
      }
    }
  };

  // ── Effects ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isEditVehicle) {
      handleAddress();
      if (shippingAddress) validateAndSetShippingAddress(shippingAddress, { silent: true });
    } else {
      (async () => {
        await handleAddress();
        if (autoValidateShippingAddress && shippingAddress) validateAndSetShippingAddress(shippingAddress, { silent: true });
      })();
    }
  }, []);

  useEffect(() => {
    if (!isEditVehicle || !originalValues.address1) return;
    const fieldsChanged = historicalAddress.value !== originalValues.historicalAddress;
    if (onFieldsChanged) {
      onFieldsChanged(fieldsChanged, {
        isAddressValidated: verified,
        isAddressNewAndNotValidated: historicalAddress.value === "Add New" && !verified,
      });
    }
  }, [historicalAddress.value, verified]);

  useEffect(() => {
    if (address1.success && !address2.error && city.success && state.success && country.success && zip.success) {
      if (verified) {
        historicalAddress.value.includes("Please")
          ? isAddressFieldsDisable && setIsAddressFieldsDisable(false)
          : showHistoricalAddressDropdown && !isAddressFieldsDisable && setIsAddressFieldsDisable(true);
      } else {
        if (vehicleData?.id && addressByPass === "NeedToValidateOnLoad") {
          if (!vehicleData?.addressByPass) {
            handleAddressVerification(address, true);
          } else {
            setAddress((prev) => ({ ...prev, verified: true, addressByPass: vehicleData.addressByPass, locationFound: vehicleData.locationFound }));
          }
        } else if (isAddressSuggestionShown) {
          showHistoricalAddressDropdown && !isAddressFieldsDisable && setIsAddressFieldsDisable(true);
          !isAddressValidationBtnDisabled && setIsAddressValidationBtnDisabled(true);
          if (!suggestedAddress.address && isAddressSuggestionShown && !dpvStatus) {
            setIsAddressSuggestionShown(false);
            setAddress((prev) => ({ ...prev, verified: true }));
          }
        } else if (
          historicalAddress.value &&
          !historicalAddress.value.includes("Please") &&
          historicalAddress.success &&
          !dpvStatus &&
          !skipAutoValidateRef.current
        ) {
          const isExistingDropdownAddress = showHistoricalAddressDropdown && selectedAddressType !== "Add New";
          handleAddressVerification(address, isExistingDropdownAddress);
        } else {
          if (!isAddressSuggestionShown && !dpvStatus) {
            isAddressFieldsDisable && setIsAddressFieldsDisable(false);
            isAddressValidationBtnDisabled && setIsAddressValidationBtnDisabled(false);
          }
        }
      }
    } else {
      if (addressByPass === "NeedToValidateOnLoad") {
        setAddress((prev) => ({ ...prev, addressByPass: null }));
      } else if (!suggestedAddress.address && isAddressSuggestionShown && !dpvStatus) {
        setIsAddressSuggestionShown(false);
        setAddress((prev) => ({ ...prev, verified: false }));
      } else if (isAddressSuggestionShown) {
        // suggestion is shown — keep fields locked, do nothing
      } else if (dpvStatus) {
        isAddressFieldsDisable && setIsAddressFieldsDisable(false);
        isAddressValidationBtnDisabled && setIsAddressValidationBtnDisabled(false);
      } else if (skipAutoValidateRef.current) {
        if (!showHistoricalAddressDropdown || selectedAddressType) {
          isAddressFieldsDisable && setIsAddressFieldsDisable(false);
          isAddressValidationBtnDisabled && setIsAddressValidationBtnDisabled(false);
        }
      } else {
        if (!historicalAddress.value) {
          showHistoricalAddressDropdown && !isAddressFieldsDisable && setIsAddressFieldsDisable(true);
        } else {
          !historicalAddress.value.includes("Please") &&
            setAddress((prev) => ({ ...prev, historicalAddress: { ...prev.historicalAddress, error: true, success: false } }));
          isAddressFieldsDisable && setIsAddressFieldsDisable(false);
          !isAddressValidationBtnDisabled && setIsAddressValidationBtnDisabled(true);
        }
      }
    }
  }, [
    verified,
    historicalAddress.value,
    addressByPass,
    address1.value,
    address2.value,
    country.value,
    city.value,
    state.value,
    zip.value,
    suggestedAddress.address,
    dpvStatus,
    isAddressSuggestionShown,
  ]);

  useEffect(() => {
    const isAddNew = selectedAddressType === "Add New";
    const isDpvPartial = dpvStatus === "S" || dpvStatus === "D" || isInSdFlow;
    if ((!isAddNew && !isDpvPartial) || !address1.value) {
      setLiveAddressPreview("");
      return;
    }
    const parts = [address1.value];
    if (address2.value) parts.push(address2.value);
    if (city.value) parts.push(city.value);
    if (state.value?.erpStateCode) parts.push(state.value.erpStateCode);
    if (zip.value) parts.push(zip.value);
    if (country.value?.code) parts.push(country.value.code);
    setLiveAddressPreview(parts.join(", ").toUpperCase());
  }, [selectedAddressType, dpvStatus, isInSdFlow, address1.value, address2.value, city.value, state.value, zip.value, country.value]);

  // ── Render helpers ─────────────────────────────────────────────────────

  const InputComp = CustomInput;
  const SelectComp = CustomDropdown;
  const radioWidth = styles.radioGroupWidth || "100%";
  const isFieldsDisabled = isAddressFieldsDisable;
  const getFieldDisabled = (fieldName) => {
    if (isValidatingNewAddress || isValidatingHistoricalAddress) return true;
    if (isAddressSuggestionShown) return true;
    if (dpvStatus) {
      if (dpvStatus === "S" || dpvStatus === "D" || dpvStatus === "Y_ADDR2") return fieldName !== "Address 2";
      if (dpvStatus === "M" || dpvStatus === "N") return fieldName === "State" || fieldName === "Country";
    }
    return isFieldsDisabled;
  };

  const shouldShowFields = (() => {
    if (isAddressSuggestionShown || dpvStatus) return true;
    if (isValidatingNewAddress) return true;
    if (variant === "vehicle" || variant === "wt") return selectedAddressType === "Add New" || (!isAddressFieldsDisable && !verified);
    if (isEditVehicle)
      return (
        !isValidatingHistoricalAddress &&
        (selectedAddressType === "Add New" || (!isAddressFieldsDisable && (isErrorInAddressFields || handleInvalidAddress)))
      );
    return true;
  })();

  const countryOptions = (
    <>
      <option disabled>Please Select</option>
      {country.options.map((c) => (
        <option key={c.code}>{c.code}</option>
      ))}
    </>
  );
  const stateOptionsEl = (
    <>
      <option disabled style={{ backgroundColor: "lightgrey" }}>
        Please Select
      </option>
      {state.options.map((sc) => (
        <option key={sc.stateCode + sc.countryCode}>{sc.stateName}</option>
      ))}
    </>
  );

  const addr1Props = {
    type: "text",
    label: "Address 1",
    "aria-label": "Address 1",
    error: address1.error,
    errorText: address1.errorText,
    success: getFieldDisabled("Address 1") ? false : address1.success,
    value: address1.value,
    disabled: getFieldDisabled("Address 1"),
    onChange: (e) => handleAddressFields("Address 1", e.target.value),
    onBlur: (e) => handleAddressFields("Address 1", e.target.value?.trim(), false, false, false, false, true),
  };
  const addr2Props = {
    type: "text",
    label: "Address 2 (Optional)",
    "aria-label": "Address 2 (Optional)",
    error: address2.error,
    errorText: address2.errorText,
    success: getFieldDisabled("Address 2") ? false : address2.success,
    value: address2.value,
    disabled: getFieldDisabled("Address 2"),
    onChange: (e) => {
      skipAutoValidateRef.current = true;
      handleAddressFields("Address 2", e.target.value);
    },
    onBlur: (e) => handleAddressFields("Address 2", e.target.value?.trim(), false, false, false, false, true),
  };
  const cityProps = {
    type: "text",
    label: "City",
    "aria-label": "City",
    error: city.error,
    errorText: city.errorText,
    success: getFieldDisabled("City") ? false : city.success,
    value: city.value,
    disabled: getFieldDisabled("City"),
    onChange: (e) => handleAddressFields("City", e.target.value),
    onBlur: (e) => handleAddressFields("City", e.target.value?.trim(), false, false, false, false, true),
  };
  const countryFieldProps = {
    label: "Country",
    "aria-label": "Country",
    hideErrorImg: country.error,
    error: country.error,
    errorText: country.errorText,
    success: getFieldDisabled("Country") ? false : country.success,
    value: country.value?.code || "Please Select",
    disabled: getFieldDisabled("Country"),
    onChange: (e) => handleAddressFields("Country", e.target.value, false, false),
  };
  const stateFieldProps = {
    label: "State",
    "aria-label": "State",
    disabled: getFieldDisabled("State") || !country.value,
    hideErrorImg: state.error,
    error: state.error,
    errorText: getFieldDisabled("State") ? "" : state.errorText,
    success: getFieldDisabled("State") ? false : state.success,
    value: state.value?.stateName || "Please Select",
    onChange: (e) => handleAddressFields("State", e.target.value.split(" - ")[0], state.options),
  };
  const zipFieldProps = {
    type: "text",
    label: "Zip/Postal Code",
    "aria-label": "Zip",
    error: zip.error,
    errorText: zip.errorText,
    value: zip.value,
    success: getFieldDisabled("Zip") ? false : zip.success,
    disabled: getFieldDisabled("Zip") || !country.value,
    onChange: (e) => handleAddressFields("Zip", e.target.value, zipRegex),
    maxLength: zipCodeLimit || undefined,
    onBlur: (e) => handleAddressFields("Zip", e.target.value?.trim(), zipRegex, false, false, false, true),
  };

  const renderFormFields = () => (
    <>
      <div className="sl-addr-field">
        <InputComp {...addr1Props} formWidth="100%" width="100%" />
      </div>
      <div className="sl-addr-field">
        <InputComp {...addr2Props} formWidth="100%" width="100%" />
      </div>
      <div className="sl-addr-grid">
        <div className="sl-addr-field">
          <InputComp {...cityProps} formWidth="100%" width="100%" />
        </div>
        <div className="sl-addr-field">
          <SelectComp {...countryFieldProps}>{countryOptions}</SelectComp>
        </div>
        <div className="sl-addr-field">
          <SelectComp {...stateFieldProps}>{stateOptionsEl}</SelectComp>
        </div>
        <div className="sl-addr-field">
          <InputComp {...zipFieldProps} formWidth="100%" width="100%" />
        </div>
      </div>
    </>
  );

  const renderHistoricalDropdown = () => {
    if (!showHistoricalAddressDropdown) return null;
    const ADDR_PLACEHOLDER = "Please select an address or choose Add New to add a new address";
    const rawDropdownValue = variant === "wt" ? selectedAddressType : historicalAddress.value || ADDR_PLACEHOLDER;
    const dropdownValue =
      selectedAddressType === "Add New" && liveAddressPreview
        ? liveAddressPreview
        : (dpvStatus === "S" || dpvStatus === "D" || isInSdFlow) && liveAddressPreview
        ? liveAddressPreview
        : rawDropdownValue && rawDropdownValue !== ADDR_PLACEHOLDER && rawDropdownValue !== "Add New"
        ? rawDropdownValue.toUpperCase()
        : rawDropdownValue;
    const isValidAddr = (addr) =>
      !!addr && addr.trim() !== "" && !addr.trim().startsWith(",") && addr.split(",").filter((p) => p.trim() !== "").length >= 4;
    const addressList = (() => {
      const list = [];
      if (isEditVehicle && isValidAddr(shippingAddress)) list.push(shippingAddress.toUpperCase());
      if (historicalAddressDetails?.historicalAddressDetails?.length > 0) {
        historicalAddressDetails.historicalAddressDetails.forEach((addr) => {
          const upper = addr.toUpperCase();
          const notDup = !list.some((a) => a.toUpperCase() === upper);
          if (notDup && (isEditVehicle ? isValidAddr(addr) : true)) list.push(upper);
        });
      }
      return list;
    })();
    const dropdownOptions = [];
    if (!isEditVehicle && variant !== "wt") {
      dropdownOptions.push(
        <option key="default-placeholder" disabled>
          Please select an address or choose Add New to add a new address
        </option>
      );
    }
    if (addressList.length > 0) {
      addressList.forEach((addr) => dropdownOptions.push(<option key={addr}>{addr}</option>));
    } else {
      dropdownOptions.push(
        <option key="no-data" disabled>
          No Address Available
        </option>
      );
    }
    if ((selectedAddressType === "Add New" || dpvStatus === "S" || dpvStatus === "D" || isInSdFlow) && liveAddressPreview) {
      dropdownOptions.push(<option key="live-preview">{liveAddressPreview}</option>);
    }
    dropdownOptions.push(<option key="add-new">Add New</option>);
    return (
      <div className="sl-addr-field" style={{ marginTop: variant === "wt" ? "0" : "4px" }}>
        <SelectComp
          formWidth="100%"
          width="100%"
          label="Shipping Address *"
          data-testid="select-shipping-address"
          type="text"
          disabled={isValidatingHistoricalAddress || isValidatingNewAddress || !country.options.length || !state.allStateOptions}
          aria-label={dropdownValue}
          value={dropdownValue}
          onChange={(e) => handleHistoricalAddressSelection(e.target.value)}
        >
          {dropdownOptions}
        </SelectComp>
      </div>
    );
  };

  const renderSuggestions = () => {
    if (!isAddressSuggestionShown) return null;
    return (
      <div>
        <UltraMediumText eDSFont fontSize="18px" marginBottom="0" marginTop="1rem">
          Choose an option
        </UltraMediumText>
        <SmallText>We couldn&apos;t verify the entered address. We found a close match</SmallText>
        <RadioButtonGroupWrapper $width={radioWidth}>
          <RadioButtonGroup
            onChange={() => handleAddressSelection(true)}
            error={false}
            data={[
              {
                name: "group",
                label: (
                  <>
                    <span>Use suggested address</span>
                    <mark css={{ fontSize: "10px", backgroundColor: "#d8dada", padding: "0rem 0.313rem", marginLeft: "0.313rem" }}>Verified</mark>
                  </>
                ),
                children: suggestedAddress.address,
                value: "radioOne",
                ariaLabel: "Use suggested address",
              },
            ]}
          />
          {showTypedAddressOption ? (
            <RadioButtonGroup
              onChange={() => handleAddressSelection()}
              error={false}
              data={[
                {
                  name: "group",
                  label: <span>Use address as typed</span>,
                  children: typedAddress,
                  value: "radioTwo",
                  ariaLabel: "Use address as typed",
                },
              ]}
            />
          ) : (
            <div style={{ display: "none" }}>
              <RadioButtonGroup
                onChange={() => handleAddressSelection()}
                error={false}
                data={[
                  {
                    name: "group",
                    label: <span>Use address as typed</span>,
                    children: typedAddress,
                    value: "radioTwo",
                    ariaLabel: "Use address as typed",
                  },
                ]}
              />
            </div>
          )}
        </RadioButtonGroupWrapper>
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────

  return (
    <>
      {showInternalLoader && <Loader active={isLoading} fullscreen={false} />}
      <AddressContainer $width={styles.containerWidth}>
        {showTitle && (
          <UltraMediumText css={{ marginTop: "1.25rem" }} eDSFont fontSize="18px" marginBottom="0.5rem">
            {title}
          </UltraMediumText>
        )}
        {showTitle && subTitle && (
          <UltraMediumText css={{ fontWeight: "normal" }} eDSFont fontSize="14px" marginBottom="0.5rem">
            {subTitle}
          </UltraMediumText>
        )}
        {renderHistoricalDropdown()}
        {dpvWarning && (
          <SLNotification key={generate()} type="warning" title={dpvWarning} fullBleed={true} onCloseButtonClick={() => setDpvWarning(null)} />
        )}
        {isValidatingHistoricalAddress && (
          <FlexColumn css={CustomHistoricalLoader}>
            <Loader active fullscreen={false} />
            <SmallText bold css={{ paddingLeft: "2rem" }}>
              Verifying address...
            </SmallText>
          </FlexColumn>
        )}
        <div
          style={{
            height: shouldShowFields ? "auto" : "0",
            opacity: shouldShowFields ? 1 : 0,
            overflow: shouldShowFields ? "visible" : "hidden",
            marginBottom: shouldShowFields ? "16px" : "0",
          }}
        >
          {shouldShowFields && renderFormFields()}
          <FlexRow css={{ marginTop: "8px" }}>
            {(!isAddressFieldsDisable || dpvStatus) && (!verified || isErrorInAddressFields || (isEditVehicle && handleInvalidAddress)) && (
              <CustomSLButton
                aria-label="Validate Address"
                role="button"
                onClick={() => handleAddressVerification(address)}
                disabled={(isAddressValidationBtnDisabled && !dpvStatus) || isValidatingNewAddress || isAddressSuggestionShown}
                aria-disabled={(isAddressValidationBtnDisabled && !dpvStatus) || isValidatingNewAddress || isAddressSuggestionShown}
              >
                {isValidatingNewAddress ? "Validating..." : "Validate Address"}
              </CustomSLButton>
            )}
          </FlexRow>
          {isValidatingNewAddress && (
            <FlexColumn css={CustomHistoricalLoader}>
              <Loader active fullscreen={false} />
              <SmallText bold css={{ paddingLeft: "2rem" }}>
                Validating address...
              </SmallText>
            </FlexColumn>
          )}
        </div>
        {renderSuggestions()}
      </AddressContainer>
    </>
  );
};
